import React, { useState, useMemo } from "react";
import { X, Search, Check } from "lucide-react";
import { ExtendedTemplate } from "../../../../views/marketing-pages/create-campaign/types";

interface TemplatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: ExtendedTemplate[];
  onSelect: (template: ExtendedTemplate) => void;
  title?: string;
  subtitle?: string;
}

export const TemplatePickerModal: React.FC<TemplatePickerModalProps> = ({
  isOpen,
  onClose,
  templates,
  onSelect,
  title = "Choose a Template",
  subtitle = "Choose a pre-approved template to send",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("Welcome Templates");
  const [selectedId, setSelectedId] = useState<string | null>(null); // For visual feedback

  const tabs = [
    "Welcome Templates",
    "Lead Capture / Inquiry",
    "Festival Templates",
    "Reminders",
    "Notifications & Alerts",
  ];

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      const matchesSearch =
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.preview.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [templates, searchTerm]);

  const handleSelect = (template: ExtendedTemplate) => {
    setSelectedId(template.id);
    
    // Brief visual feedback before closing
    setTimeout(() => {
      onSelect(template);
      onClose();
      setSelectedId(null); // Reset for next time
    }, 180);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-8 py-6 border-b dark:border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-gray-100">{title}</h2>
            <p className="text-slate-500 dark:text-gray-400 mt-1">{subtitle}</p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300 transition-colors"
          >
            <X size={28} />
          </button>
        </div>

        {/* Search Bar */}
        <div className="px-8 py-5 border-b dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60">
          <div className="relative max-w-md">
            <Search className="absolute left-4 top-3.5 text-slate-400 dark:text-gray-500" size={20} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 dark:text-gray-100 rounded-xl focus:outline-none focus:border-indigo-500 text-sm"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="px-8 border-b dark:border-gray-800 bg-slate-50 dark:bg-gray-800/60 overflow-x-auto">
          <div className="flex gap-8 text-sm font-medium whitespace-nowrap">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 pt-4 border-b-2 transition-all ${
                  activeTab === tab
                    ? "border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="flex-1 overflow-auto p-8">
          {filteredTemplates.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredTemplates.map((template) => {
                const isSelected = selectedId === template.id;

                return (
                  <button
                    key={template.id}
                    onClick={() => handleSelect(template)}
                    className={`group border-2 rounded-2xl overflow-hidden transition-all duration-200 bg-white dark:bg-gray-800 text-left relative ${
                      isSelected 
                        ? "border-indigo-600 dark:border-indigo-400 shadow-xl scale-[1.02]" 
                        : "border-slate-200 dark:border-gray-700 hover:border-indigo-300 dark:hover:border-indigo-500/50 hover:shadow-xl"
                    }`}
                  >
                    {/* Preview Image */}
                    <div className="aspect-video bg-slate-100 dark:bg-gray-700 relative">
                      {template.snapshot_url ? (
                        <img
                          src={template.snapshot_url}
                          alt={template.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 dark:from-gray-700 dark:via-gray-800 dark:to-gray-700">
                          <div className="text-5xl font-bold text-slate-300 dark:text-gray-600 tracking-[4px]">
                            HELLO
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <div className="font-semibold text-lg leading-tight mb-2 line-clamp-1">
                        {template.name}
                      </div>
                      <p className="text-sm text-slate-600 dark:text-gray-400 line-clamp-3 mb-4">
                        {template.preview}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        <span className="inline-block text-xs px-3 py-1 bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400 rounded-full font-medium">
                          {template.wa_status}
                        </span>
                        <span className="inline-block text-xs px-3 py-1 bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300 rounded-full">
                          {template.category}
                        </span>
                        <span className="inline-block text-xs px-3 py-1 bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300 rounded-full">
                          {template.language}
                        </span>
                      </div>
                    </div>

                    {/* Selected Checkmark */}
                    {isSelected && (
                      <div className="absolute top-4 right-4 bg-indigo-600 text-white rounded-full p-1 shadow-lg">
                        <Check size={18} strokeWidth={3} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-slate-400 dark:text-gray-500 text-lg">No templates found</p>
              <p className="text-slate-500 dark:text-gray-400 mt-2">Try adjusting your search term</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};